import { bool, func, number, object, string } from "prop-types";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

import InvisibleFileInput from "./components/InvisibleInput";
import VisibleContents from "./components/VisibleContents";

const FnrUploadButtonContext = createContext();
const useFnrUploadButtonContext = () => useContext(FnrUploadButtonContext);

/**
 * Use this component to prompt the user to upload a file. Also allows for deleting, uploading, and replacing files.
 */
const FnrUploadButton = forwardRef(
  /**
   * @param {object} param0
   * @param {string} param0.documentName - defaulting to "Document", this sting informs the user what they should be uploading. i.e. "Photo" or "Glasses Perscription"
   * @param {function} param0.onChange - the onChange handler, which can be async. Explicitly return false to prevent the field from setting the value. Return a string to display the new fileName.
   * @param {string} param0.initValue - if a file already exists, pass in a string representing it.
   * @param {color} param0.color - the color to be used for icons through the component. Atom level default apply.
   * @param {string} param0.icon - Overrides the upload symbol with whatever is passed in.
   * @param {boolean} param0.compact - compactifies the component to fit into a smaller space.
   * @param {boolean} param0.noShadeOnHover - turns off hover/focus effect.
   * @param {boolean} param0.confirmDelete - set to true to ask the user for confirmation before deleting.
   * @param {function} param0.onDelete - called when the user clicks the delete button that appears when the input has value.
   * @param {boolean} param0.hideDelete - sometime you don't want to show the delete button. Maybe your had to make a custom one, for example.
   * @param {string} param0.href - transforms the value display into a link used for downloading the file.
   * @param {number} param0.progressPercentage - pass in the current progress of the upload as a float.
   * @param {boolean} param0.readOnly - set to true when only downloading the file or displaying the name is allowed.
   * @param {object} param0.typographyProps - set props to be passed down to FnrTypography, overriding the defaults
   * @param {function} param0.onEditClick - called when the user clicks the edit button that appears when readOnly is true and this is passed.
   * @param {boolean} param0.hide - For when you want the functionality via the ref without displaying anything.
   * @param {boolean} param0.underlineOnHover - matches legacy effect of only underlining link text on hover.
   * @param {boolean} param0.showDownloadIcon - displays a download icon next to link
   * @param {boolean} param0.showCheckIcon - displays a green check icon next to the value text
   * @param {import("react").Ref} fileInputRef - passed in refs will go to an invisible <input type='file' />
   * @returns
   */
  (
    {
      documentName = "Document",
      onChange,
      initValue,
      color,
      icon,
      compact,
      noShadeOnHover,
      confirmDelete,
      onDelete,
      hideDelete,
      href,
      progressPercentage,
      readOnly,
      typographyProps = {},
      onEditClick,
      hide,
      underlineOnHover,
      showDownloadIcon,
      showCheckIcon,
    },
    fileInputRef
  ) => {
    const [displayName, setDisplayName] = useState();
    const [hasValue, setHasValue] = useState(Boolean(initValue));
    const localRef = useRef();
    const inputRef = fileInputRef || localRef;

    const containerRef = useRef();
    const isHoverFocused = useIsHoverFocused(containerRef);

    useEffect(() => {
      setHasValue(Boolean(initValue));
    }, [initValue]);

    return (
      <FnrUploadButtonContext.Provider
        value={{
          documentName,
          onChange,
          isHoverFocused,
          color,
          compact,
          confirmDelete,
          href,
          setHasValue,
          hasValue,
          initValue,
          onDelete,
          hideDelete,
          inputRef,
          containerRef,
          displayName,
          setDisplayName,
          icon,
          noShadeOnHover,
          progressPercentage,
          readOnly,
          typographyProps,
          onEditClick,
          hide,
          underlineOnHover,
          showDownloadIcon,
          showCheckIcon,
        }}
      >
        <InvisibleFileInput />
        <VisibleContents />
      </FnrUploadButtonContext.Provider>
    );
  }
);

FnrUploadButton.propTypes = {
  onChange: func.isRequired,
  documentName: string,
  color: string,
  initValue: string,
  icon: string,
  compact: bool,
  noShadeOnHover: bool,
  confirmDelete: bool,
  href: string,
  onDelete: func,
  hideDelete: bool,
  progressPercentage: number,
  readOnly: bool,
  typographyProps: object,
  onEditClick: func,
  hide: bool,
  underlineOnHover: bool,
  showDownloadIcon: bool,
  showCheckIcon: bool,
};

export { useFnrUploadButtonContext };
export default FnrUploadButton;
